import React from "react";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Eye,
  Compass,
  Sparkles,
  Brain,
  Users,
} from "lucide-react";

const RecognitionSection = () => {
  const { t } = useTranslation();

  return (
    <section id="accueil" className="py-12 bg-gradient-to-b from-soft-gray to-cream-white relative overflow-hidden">
      {/* BACKGROUND DECOR */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg
          className="absolute top-0 left-0 w-full h-full opacity-5"
          viewBox="0 0 1920 1080"
        >
          <path
            d="M0,300 Q400,200 800,320 Q1200,440 1600,280 Q1800,200 1920,250 L1920,1080 L0,1080 Z"
            fill="url(#organicGradient)"
          />
          <defs>
            <linearGradient
              id="organicGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#C4B5A0" />
              <stop offset="100%" stopColor="#D4C4B0" />
            </linearGradient>
          </defs>
        </svg>

        <div className="absolute top-40 left-1/4 opacity-10">
          <Heart className="h-24 w-24 text-warm-sage rotate-12 animate-pulse" />
        </div>
        <div className="absolute top-60 right-1/3 opacity-15">
          <Brain
            className="h-20 w-20 text-soft-taupe -rotate-12 animate-pulse"
            style={{ animationDelay: "1s" }}
          />
        </div>
        <div className="absolute bottom-40 right-1/4 opacity-12">
          <Users
            className="h-16 w-16 text-warm-sage rotate-45 animate-pulse"
            style={{ animationDelay: "2s" }}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* HEADER */}
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center mb-8 group">
            <div className="flex items-center mb-4">
              <div className="h-px bg-gradient-to-r from-transparent via-warm-sage to-transparent w-24 mr-6 group-hover:w-32 transition-all duration-700" />
              <div className="relative">
                <div className="absolute inset-0 bg-warm-sage/20 rounded-full blur-xl" />
              </div>
              <h2 className="heading-fluid font-serif font-light text-charcoal tracking-tight text-center">
                {t("recognition.title")}
              </h2>
              <div className="h-px bg-gradient-to-r from-transparent via-warm-sage to-transparent w-24 ml-6 group-hover:w-32 transition-all duration-700" />
            </div>

            <div className="relative max-w-2xl mx-auto">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-soft-taupe to-transparent" />
              
              {/* --- CHANGEMENT ICI --- */}
              <p className="subhead-fluid text-warm-sage italic font-serif font-normal text-center tracking-wide">
                {t("recognition.subtitle")}
              </p>
              {/* --- FIN DU CHANGEMENT --- */}

            </div>
          </div>
        </div>

        {/* CORPS GRILLE */}
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* TEXTE GAUCHE */}
          <div className="space-y-10">
            <div className="relative">
              {/* ligne verticale décor */}
              <div className="absolute -left-8 top-0 w-2 h-32 bg-gradient-to-b from-warm-sage via-soft-taupe to-transparent rounded-full" />

              <div className="prose prose-lg max-w-none space-y-8">
                <div className="group">
                  <p className="text-xl text-charcoal leading-relaxed mb-6 font-light transform group-hover:translate-x-2 transition-transform duration-500">
                    {t("recognition.p1")}
                  </p>
                  <div className="w-16 h-px bg-warm-sage/40 group-hover:w-24 transition-all duration-500" />
                </div>

                <div className="group">
                  
                  {/* --- CHANGEMENT ICI --- */}
                  <p className="text-xl text-charcoal leading-relaxed mb-6 font-serif italic transform group-hover:translate-x-2 transition-transform duration-500 delay-100">
                    {t("recognition.p2")}
                  </p>
                  {/* --- FIN DU CHANGEMENT --- */}
                  
                  <div className="w-16 h-px bg-soft-taupe/40 group-hover:w-24 transition-all duration-500 delay-100" />
                </div>

                <div className="group">
                  <p className="text-xl text-charcoal leading-relaxed mb-8 font-light transform group-hover:translate-x-2 transition-transform duration-500 delay-200">
                    {t("recognition.p3")}
                  </p>
                  <div className="w-16 h-px bg-warm-sage/60 group-hover:w-24 transition-all duration-500 delay-200" />
                </div>
              </div>

              {/* CITATION */}
              <div className="relative bg-gradient-to-r from-warm-sage/5 via-warm-sage/10 to-warm-sage/5 p-12 rounded-2xl border border-warm-sage/20 mt-12 group hover:scale-105 transition-all duration-700">
                <div className="absolute -top-4 left-8">
                  <Sparkles className="h-8 w-8 text-warm-sage" />
                </div>
                <blockquote className="text-2xl font-serif font-light text-charcoal italic text-center relative">
                  <span className="text-warm-sage text-6xl absolute -top-4 -left-4 opacity-30">
                    “
                  </span>
                  {t("recognition.quote")}
                  <span className="text-warm-sage text-6xl absolute -bottom-8 -right-4 opacity-30">
                    ”
                  </span>
                </blockquote>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-12 h-12 bg-warm-sage rounded-full flex items-center justify-center">
                  <Heart className="h-6 w-6 text-cream-white animate-pulse" />
                </div>
              </div>
            </div>
          </div>

          {/* VISUEL DROIT */}
          <div className="relative group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all duration-700">
              <img
                src="/Images/wave.avif"
                alt={t("recognition.imageAlt")}
                className="w-full h-[700px] object-cover object-center transform group-hover:scale-130 transition-transform duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-warm-sage/20" />

              {/* icônes au survol */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute top-8 left-8 bg-cream-white/90 backdrop-blur-sm p-4 rounded-full shadow-lg shadow-warm-sage/20">
                  <Eye className="h-6 w-6 text-warm-sage animate-pulse" />
                </div>
                <div className="absolute bottom-8 right-8 bg-cream-white/90 backdrop-blur-sm p-4 rounded-full shadow-lg shadow-soft-taupe/20">
                  <Compass
                    className="h-6 w-6 text-soft-taupe animate-spin"
                    style={{ animationDuration: "3s" }}
                  />
                </div>
              </div>
            </div>

            {/* halos flottants */}
            <div className="absolute -top-8 -left-8 w-16 h-16 bg-warm-sage/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-700" />
            <div className="absolute -bottom-8 -right-8 w-20 h-20 bg-soft-taupe/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-700 delay-200" />

            {/* coins fins */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-warm-sage rounded-tl-lg -translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-500" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-warm-sage rounded-br-lg translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-500 delay-200" />
          </div>
        </div>

        {/* BLOC EDITORIAL LUXE */}
        <div className="mt-20 relative">
          <div className="max-w-5xl mx-auto">
            {/* séparateur décoratif */}
            <div className="flex items-center justify-center mb-12">
              <div className="h-px bg-gradient-to-r from-transparent via-warm-sage to-soft-taupe w-32" />
              <div className="mx-8 p-3 bg-warm-sage/10 rounded-full">
                <Heart className="h-6 w-6 text-warm-sage animate-pulse" />
              </div>
              <div className="h-px bg-gradient-to-r from-soft-taupe via-warm-sage to-transparent w-32" />
            </div>

            <h3 className="heading-fluid font-serif font-light text-charcoal text-center mb-12 tracking-tight">
              {t("recognition.previewTitle")}
            </h3>

            {/* intro texte */}
            <div className="max-w-4xl mx-auto text-center mb-16">
              <p className="text-[17px] md:text-xl leading-[1.8] font-light text-charcoal ">
                {t("recognition.previewP1")}
              </p>
              <p className="text-[17px] md:text-xl leading-[1.8] font-light text-warm-gray mt-8">
                {t("recognition.previewP2")}
              </p>
            </div>

            {/* deux cartes éditoriales */}
            <div className="relative max-w-6xl mx-auto">
              <div className="absolute inset-0 blur-[60px] opacity-40 rounded-[3rem] bg-gradient-to-br from-warm-sage/20 via-soft-taupe/10 to-transparent pointer-events-none" />

              <div className="relative grid gap-8 md:gap-10 md:grid-cols-2">
               
 
              </div>
            </div>

            {/* CTA */}
            <div className="mt-16 text-center">
              <button
                onClick={() =>
                  document
                    .getElementById("approche")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="group relative bg-gradient-to-r from-warm-sage to-soft-taupe text-cream-white font-medium px-12 py-4 rounded-full hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-2xl overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  {t("recognition.cta")}
                  <Compass className="ml-3 h-5 w-5 group-hover:rotate-180 transition-transform duration-700" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-soft-taupe to-warm-sage -translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
              </button>
            </div>

           
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecognitionSection;